import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';

import type { Modifier } from '@popperjs/core';
import cx from 'classnames';
import { motion } from 'framer-motion';

import { Permissions } from '@cloud-wave/neon-common-lib';

import { useAIContext } from 'lib/common/contexts/AIContext';
import IQMenuContainer from 'lib/common/contexts/AIContext/components/IQMenuContainer/IQMenuContainer';
import { AskIQHeading } from 'lib/common/contexts/AIContext/components/common/AskIQHeading/AskIQHeading';
import { IQDisplayMode } from 'lib/common/contexts/AIContext/components/constants/IQDisplayMode';
import { usePermissionsContext } from 'lib/common/contexts/PermissionsContext';
import layoutStyles from 'lib/common/contexts/layout/LayoutContext/components/Layout/layout.module.scss';
import { useSoftphoneLayout } from 'lib/common/contexts/layout/SoftphoneLayoutContext';

import { TPopperPosition } from '../../types/TPopperPosition';
import ActionSheet from './components/ActionSheet';
import IQShortcutMenu from './components/IQShortcutMenu';

import styles from './iq-text-selection-popper.module.scss';

const MAX_WIDTH_FOR_SMALL_MENU = 500;
const MIN_SPACE_FROM_TOP_OR_BOTTOM = 300;

// Re-run Popper whenever popper/reference size changes
const observeResize: Modifier<'observeResize', any> = {
  name: 'observeResize',
  enabled: true,
  phase: 'write',
  fn: () => {},
  effect: ({ state, instance }) => {
    if (!('ResizeObserver' in window)) {
      return () => {};
    }

    const pop = state.elements.popper as HTMLElement | null;
    const ref = state.elements.reference as HTMLElement | null;

    let rafId: number | null = null;

    const scheduleUpdate = () => {
      if (rafId !== null) {
        return;
      }

      rafId = requestAnimationFrame(() => {
        rafId = null;
        instance.update();
      });
    };

    const ro = new ResizeObserver(() => scheduleUpdate());

    if (pop) {
      ro.observe(pop);
    }

    if (ref) {
      ro.observe(ref);
    }

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      ro.disconnect();
    };
  },
};

const centerOnAnchorWhenNoSpace: Modifier<
  'centerOnAnchorWhenNoSpace',
  {
    minSpace?: number;
    padding?: number;
  }
> = {
  name: 'centerOnAnchorWhenNoSpace',
  enabled: true,
  phase: 'main',
  requires: ['popperOffsets', 'computeStyles'],
  fn: ({ state, options }) => {
    if (window.innerWidth <= MAX_WIDTH_FOR_SMALL_MENU) {
      return;
    }

    const { minSpace = MIN_SPACE_FROM_TOP_OR_BOTTOM, padding = 8 } = options || {};
    const refRect = state.rects.reference;
    const popRect = state.rects.popper;

    const spaceAbove = refRect.y - padding;
    const spaceBelow = window.innerHeight - (refRect.y + refRect.height + padding);

    const insufficientAbove = spaceAbove < minSpace;
    const insufficientBelow = spaceBelow < minSpace;

    if (insufficientAbove && insufficientBelow) {
      const popperOffsets = state.modifiersData.popperOffsets;
      if (!popperOffsets) {
        return;
      }

      const centeredX = refRect.x + refRect.width / 2 - popRect.width / 2 + minSpace / 2;
      const centeredY = refRect.y + refRect.height / 2 - popRect.height / 2 - minSpace / 2;

      popperOffsets.y = centeredY;
      popperOffsets.x = centeredX;

      state.attributes.popper = {
        ...(state.attributes.popper || {}),
        'data-centered-over-anchor': 'true',
      };
    }
  },
};

const getTransformClamped = ({
  contentWidth,
  transform,
  isSmallMenu,
}: {
  contentWidth: number;
  transform: string | undefined;
  isSmallMenu: boolean;
}) => {
  if (isSmallMenu) {
    return transform;
  }
  const clampedLeft = contentWidth - 300;
  // https://regex101.com/r/e4I67M/1
  const transformsMatch = transform?.match(/[0-9|.?]*px/)?.[0];
  const transformToInt = Number.parseInt(transformsMatch?.replace('px', '') || '0');

  return transformToInt > clampedLeft ? transform?.replace(transformsMatch || '', `${clampedLeft}px`) : transform;
};

export default function IQTextSelectionPopper({
  popperPosition,
  selectedText,
  onClosePopper,
  replaceTextCallback,
}: {
  selectedText: string | null;
  popperPosition: TPopperPosition | null;
  onClosePopper: () => void;
  replaceTextCallback?: (text: string) => void;
}) {
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const { contentWidth } = useSoftphoneLayout();

  const { hasPermission } = usePermissionsContext();

  const isForcedSoftphone = hasPermission({ permission: Permissions.SOFTPHONE_ONLY });

  const containerRef = useRef<HTMLDivElement>(null);

  const isSmallMenu = contentWidth < MAX_WIDTH_FOR_SMALL_MENU;

  const {
    state: { isActionMenuOpen, replaceTextCallbackRef, selectedAction },
    actions: { setIQInput, closeActionMenu, openActionMenu, resetIQActionMenuState },
  } = useAIContext();

  const {
    styles: popperStyles,
    attributes,
    update,
  } = usePopper(referenceElement, popperElement, {
    placement: 'auto',
    strategy: 'fixed',
    modifiers: [
      { name: 'offset', options: { offset: [0, 8] } },
      observeResize,
      {
        name: 'flip',
        options: {
          padding: { top: 140, bottom: 200 },
          rootBoundary: 'viewport',
          flipVariations: true,
          allowedAutoPlacements: ['bottom', 'top', 'left'],
        },
      },
      {
        name: 'preventOverflow',
        options: { padding: 8, rootBoundary: 'viewport', tether: false, altBoundary: false },
      },
      {
        name: 'computeStyles',
        options: { gpuAcceleration: true, roundOffsets: true },
      },
      { ...centerOnAnchorWhenNoSpace, options: { minSpace: MIN_SPACE_FROM_TOP_OR_BOTTOM, padding: 8 } },
      { name: 'eventListeners', options: { scroll: true, resize: true } },
    ],
  });

  useEffect(() => {
    if (popperPosition) {
      update?.();
    }
  }, [popperPosition, update]);

  // Close popper when clicking outside container
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClosePopper();
        closeActionMenu();
        resetIQActionMenuState();
      }
    };
    document.addEventListener('mousedown', handleClickOutside, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [containerRef]);

  useEffect(() => {
    update?.();
  }, [isActionMenuOpen, isSmallMenu, selectedAction, update]);

  const handleAskIQClick = () => {
    if (selectedText) {
      replaceTextCallbackRef.current = replaceTextCallback;
      setIQInput(selectedText);
    }

    openActionMenu();
  };

  if (!popperPosition || !selectedText) {
    return null;
  }

  const isActionSheetOpen = isActionMenuOpen && isSmallMenu;
  const { left, top, width, height } = popperPosition;

  const menuSizeVariants = {
    active: {
      width: '300px',
    },
    inactive: {
      width: '90px',
    },
  };

  const transform = getTransformClamped({
    contentWidth: contentWidth,
    transform: popperStyles.popper.transform,
    isSmallMenu: isSmallMenu,
  });

  const askIQPopperContent = (
    <div ref={containerRef}>
      <div
        ref={setReferenceElement}
        style={{
          opacity: 0,
          pointerEvents: 'none',
          position: 'fixed',
          left,
          top,
          width,
          height,
        }}
      />
      <motion.div
        animate={isActionMenuOpen ? 'active' : 'inactive'}
        variants={menuSizeVariants}
        transition={{
          type: 'tween',
          duration: 0.18,
          ease: 'easeInOut',
        }}
        ref={setPopperElement}
        tabIndex={-1}
        onMouseDown={(e) => e.preventDefault()}
        className={cx(styles['askiq__pop-up'], {
          [styles['askiq__pop-up--action-menu-small']]: isSmallMenu,
          [styles['askiq__pop-up--action-sheet-open']]: isActionSheetOpen,
        })}
        onUpdate={() => update?.()}
        onAnimationComplete={() => update?.()}
        style={{
          ...popperStyles.popper,
          transform,
        }}
        {...attributes.popper}
      >
          {!isActionMenuOpen && (
            <button
              data-testid="askiq-pop-up-button"
              className={styles['askiq__pop-up-button']}
              onClick={handleAskIQClick}
            >
              <AskIQHeading />
            </button>
          )}
          {isActionMenuOpen && !isSmallMenu && <IQShortcutMenu />}
      </motion.div>

      <ActionSheet
        className={cx({ [layoutStyles['layout--softphone--forced']]: isForcedSoftphone })}
        isOpen={isActionSheetOpen}
        onClose={closeActionMenu}
      >
        <IQMenuContainer displayMode={IQDisplayMode.COMFORTABLE} />
      </ActionSheet>
    </div>
  );

  return createPortal(askIQPopperContent, document.body);
}
